import { useAsync } from 'react-use';
import { useEffect } from 'react';
import { PromiseType } from 'react-use/lib/util';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { usePersistedState } from '../util/persistedState';
import { Duration } from 'date-fns';
import { ConfigHelper } from '@devxdiscover/devhub-common';

export type JiraStory = {
  jiraInstance: string; // "https://ccp.sys.comcast.net/"
  ticketId: string; // "OCP-1234"
  issueStatus: string; // "Completed"
  summary: string; // "Summary"
  created: Date; // "25-OCT-2022 13:51:40"
};

/**
 * Returns jira user stories for MyWork component
 * @param cacheDuration - The {@link Duration} after which the state should be considered expired.
 */
export const useJiraStoryRequest: (cacheDuration?: Duration) => {
  loading: boolean;
  error?: Error;
  value?: PromiseType<Promise<JiraStory[]>>;
} = (cacheDuration: Duration = { minutes: 120 }) => {
  const [userStories, setUserStories] = usePersistedState<JiraStory[]>(
    'jira-stories',
    cacheDuration,
    [],
  );
  const identityApi = useApi(identityApiRef);

  async function getBackstageIdentity() {
    return await identityApi.getBackstageIdentity();
  }

  function getLogin() {
    return getBackstageIdentity().then(async identity => {
      return identity.userEntityRef.split('/')[1];
    });
  }

  const { value, loading, error } = useAsync(async (): Promise<JiraStory[]> => {
    if ((userStories?.length ?? 0) === 0) {
      const login = await getLogin();
      const response = await fetch(
        ConfigHelper.instance.proxyUrl(`/obiee/getJiraTicketsByOwner/${login}`)
          .href,
      );

      const stories = (await response.json()) as JiraStory[];

      return stories ?? [];
    }
    return userStories;
  });

  useEffect(() => {
    if (value) {
      setUserStories(value);
    }
  }, [value, setUserStories]);

  return {
    value,
    loading,
    error,
  };
};

export type JiraElevations = {
  expand: string; // "schema,names"
  startAt: number; // 0,
  maxResults: number; // 50,
  total: number; // 22,
  issues: JiraElevation[];
};

export type JiraElevation = {
  expand: string; // operations,versionedRepresentations,editmeta,changelog,renderedFields
  id: string; // "53528",
  self: string; // "https://npselevations.sys.comcast.net/rest/api/2/issue/53528",
  key: string; // "NPS-20520",
  fields: {
    summary: string; // Expand Ed-Assist Education List to include more HBCU’s  ,
    description: string; // Currently, while reviewing bright horizon's educational institution...
    updated: string; // 2023-01-19T15:31:22.000+0000,
    status: {
      self: string; // https://npselevations.sys.comcast.net/rest/api/2/status/10400,
      description: string; // A solution for this elevation has been identified.,
      iconUrl: string; // https://npselevations.sys.comcast.net/images/icons/statuses/generic.png,
      name: string; // Awaiting Implementation,
      id: number; // 10400,
      statusCategory: {
        self: string; // https://npselevations.sys.comcast.net/rest/api/2/statuscategory/4,
        id: number; // 4,
        key: string; // indeterminate,
        colorName: string; // yellow,
        name: string; // In Progress
      };
    };
  };
};

/**
 * Returns the top 25 jira elevations (may return less if there arent 25 )
 * @param cacheDuration - The {@link Duration} after which the state should be considered expired.
 */
export const useJiraTopElevations: (cacheDuration?: Duration) => {
  loading: boolean;
  error?: Error;
  value?: PromiseType<Promise<JiraElevation[]>>;
} = (cacheDuration: Duration = { minutes: 120 }) => {
  const [jiraElevations, setJiraElevations] = usePersistedState<
    JiraElevation[]
  >('jira-elevations', cacheDuration, []);

  const { value, loading, error } = useAsync(
    async (): Promise<JiraElevation[]> => {
      if ((jiraElevations?.length ?? 0) === 0) {
        const response = await fetch(
          ConfigHelper.instance.proxyUrl(
            `/jira/search?fields=*navigatable,+status,+summary,+updated,+description,-comment&jql=project='NPS'AND'Current Top 25'='Yes'AND'status'!='Deferred - After Review'`,
          ).href,
        );
        const elevations = (await response.json()) as JiraElevations;

        return elevations?.issues ?? [];
      }
      return jiraElevations;
    },
  );

  useEffect(() => {
    if (value) {
      setJiraElevations(value);
    }
  }, [value, setJiraElevations]);

  return {
    value,
    loading,
    error,
  };
};
